/**
 * BenchmarksRefreshPanel — Owner-only panel for the public /benchmarks
 * page's monthly auto-refresh job.
 *
 * Shows per-datapoint current override value + last-refreshed-at, and a
 * manual "Refresh now" button (handy for testing after editing the seed
 * in `routers/public_benchmarks.py`). Auto-fetch covers NIST NVD + OFAC
 * + NY Fed GSCPI on the 1st of every month; manual sources surface
 * here with their cadence so you can see what's due.
 */
import { useEffect, useState } from "react";
import { RefreshCw, BarChart3, ExternalLink, CheckCircle2, XCircle, Edit3, Save, Undo2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

function ManualOverrideCell({ dp, override, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(override?.value ?? dp.value ?? "");

  const save = async () => {
    try {
      const v = typeof dp.value === "number" ? Number(draft) : String(draft);
      if (typeof dp.value === "number" && Number.isNaN(v)) {
        toast.error("Numeric value required");
        return;
      }
      await api.post("/owner/benchmarks/override", { key: dp.key, value: v });
      toast.success("Override saved");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Save failed");
    }
  };

  const clear = async () => {
    try {
      await api.post("/owner/benchmarks/clear-override", { key: dp.key });
      toast.success("Reverted to seed value");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1">
        {override ? String(override.value) : <span className="text-ek-ink/35">— (seed)</span>}
        <button
          onClick={() => { setDraft(override?.value ?? dp.value); setEditing(true); }}
          className="rounded p-0.5 text-ek-ink/40 hover:text-ek-purple"
          title="Manual override"
          data-testid={`override-edit-${dp.key}`}
        ><Edit3 size={10} /></button>
        {override && (
          <button
            onClick={clear}
            className="rounded p-0.5 text-ek-ink/40 hover:text-rose-600"
            title="Revert to seed"
            data-testid={`override-clear-${dp.key}`}
          ><Undo2 size={10} /></button>
        )}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
        className="w-20 rounded border border-ek-purple/40 px-1 py-0.5 text-right font-mono text-[11px]"
        data-testid={`override-input-${dp.key}`}
      />
      <button onClick={save} className="rounded bg-ek-purple p-0.5 text-white"
        data-testid={`override-save-${dp.key}`}><Save size={10} /></button>
      <button onClick={() => setEditing(false)} className="rounded p-0.5 text-ek-ink/40">×</button>
    </span>
  );
}

export function BenchmarksRefreshPanel() {
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/owner/benchmarks/refresh-status"),
        api.get("/public/benchmarks"),
      ]);
      const o = {};
      for (const row of r1.data.overrides || []) o[row.key] = row;
      setOverrides(o);
      setData({
        autoKeys: r1.data.auto_refresh_keys || [],
        log: r1.data.log || [],
        categories: r2.data.categories || [],
      });
    } catch (e) {
      setErr(formatApiErrorDetail(e));
    }
  };
  useEffect(() => { load(); }, []);

  const refreshNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/owner/benchmarks/refresh-now");
      const ok = Object.values(r.data.outcomes).filter((o) => o.ok).length;
      const failed = Object.values(r.data.outcomes).length - ok;
      toast.success(`Refresh complete — ${ok} ok, ${failed} failed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Refresh failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <section
      className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm"
      data-testid="benchmarks-refresh-panel"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-ek-purple/10 p-2 text-ek-purple"><BarChart3 size={18} /></div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold text-ek-ink">
            Public benchmarks — monthly refresh
          </h2>
          <p className="mt-0.5 text-sm text-ek-ink/65">
            Auto-fetches NIST NVD, OFAC Recent Actions, and NY Fed GSCPI on the
            1st of every month at 04:00 UTC. Manual sources (ENISA, FCA, SEC, ISM,
            Baltic Exchange) show their cadence so you know what&apos;s due.
          </p>
        </div>
        <button
          onClick={refreshNow}
          disabled={running}
          className="inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple/90 disabled:opacity-50"
          data-testid="benchmarks-refresh-now-button"
        >
          <RefreshCw size={14} className={running ? "animate-spin" : ""} />
          {running ? "Fetching…" : "Refresh now"}
        </button>
      </div>

      {err && <p className="mt-3 text-xs text-rose-600">{err}</p>}

      {data && (
        <div className="mt-4 space-y-4">
          {data.categories.map((cat) => (
            <div key={cat.category_id} data-testid={`refresh-category-${cat.category_id}`}>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-ek-ink/55">{cat.title}</p>
              <table className="w-full text-xs">
                <tbody>
                  {cat.datapoints.map((dp) => {
                    const ov = overrides[dp.key];
                    const isAuto = !!dp.auto_refresh;
                    return (
                      <tr key={dp.key} className="border-t border-black/5">
                        <td className="py-1.5 pr-3 text-ek-ink/75">{dp.label}</td>
                        <td className="py-1.5 pr-3 text-right font-mono text-ek-ink">
                          <ManualOverrideCell dp={dp} override={ov} onSaved={load} />
                        </td>
                        <td className="py-1.5 pr-3 text-right text-ek-ink/45">
                          {isAuto ? (
                            ov?.refreshed_at
                              ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={11} /> {ov.refreshed_at.slice(0, 10)}</span>
                              : <span className="inline-flex items-center gap-1 text-amber-700"><XCircle size={11} /> never auto-fetched</span>
                          ) : (
                            <span className="text-[10px] text-ek-ink/45">manual · {dp.cadence}</span>
                          )}
                        </td>
                        <td className="py-1.5 text-right">
                          <a href={dp.source_url} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 text-[10px] text-ek-purple hover:underline">
                            source <ExternalLink size={9} />
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
          {data.log.length > 0 && (
            <div>
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-ek-ink/55">
                Recent fetch log
              </p>
              <ul className="mt-1 max-h-32 space-y-0.5 overflow-auto text-[11px]" data-testid="benchmark-refresh-log">
                {data.log.slice(0, 10).map((r, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <span className={`inline-flex items-center gap-1 ${r.ok ? "text-emerald-700" : "text-rose-600"}`}>
                      {r.ok ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                      <span className="font-mono text-ek-ink/65">{r.key}</span>
                    </span>
                    <span className="text-ek-ink/45">
                      {r.ok ? String(r.value) : (r.error || "").slice(0, 60)}
                      <span className="ml-2 text-ek-ink/35">{r.ts?.slice(0, 19)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
